import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { BlogHero } from "@/components/BlogHero";
import { FeaturedArticle } from "@/components/FeaturedArticle";
import { BlogSearchBar } from "@/components/BlogSearchBar";
import { CategoryGrid } from "@/components/CategoryGrid";
import { Newsletter } from "@/components/Newsletter";
import { BlogPostDisplay } from "@/components/BlogPostDisplay";
import { BlogPost, BlogCategory } from "@/types/blogPost";
import { fetchPublishedBlogPosts, fetchFeaturedBlogPost, fetchBlogCategories } from "@/utils/blogOperations";

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogData = async () => {
      try {
        const [posts, featured, cats] = await Promise.all([
          fetchPublishedBlogPosts(),
          fetchFeaturedBlogPost(),
          fetchBlogCategories()
        ]);
        
        setBlogPosts(posts);
        setFeaturedPost(featured);
        setCategories(cats);
      } catch (error) {
        console.error('Error loading blog data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogData();
  }, []);

  const filteredPosts = blogPosts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (post.subtitle && post.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <BlogPostDisplay 
            post={selectedPost} 
            onBack={() => setSelectedPost(null)} 
          />
        </div>
        <Footer />
        <FloatingWhatsApp />
        <AccessibilityButton />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center">
            <p className="text-base sm:text-lg text-gray-600">Carregando posts...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <BlogHero />
      
      <FeaturedArticle 
        featuredPost={featuredPost} 
        onReadPost={setSelectedPost}
      />
      
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mx-auto mb-8 sm:mb-12">
          <BlogSearchBar 
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>
      </div>
      
      <CategoryGrid 
        categories={categories.map(cat => ({
          name: cat.name,
          color: `text-black`,
          borderColor: `border-gray-200`,
          id: cat.id,
          color_hex: cat.color_hex,
          border_color_hex: cat.border_color_hex,
          text_color_hex: cat.text_color_hex
        }))}
        filteredPosts={filteredPosts}
        onPostSelect={setSelectedPost}
      />
      
      {/* Load More */}
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mt-8 sm:mt-12 mb-8 sm:mb-12">
          <Button className="bg-green-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-accent hover:bg-green-700 transition-colors text-sm sm:text-base w-full sm:w-auto max-w-xs">
            Carregar Mais Artigos
          </Button>
        </div>
      </div>

      <Newsletter />
      
      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
